import { useEffect, useState } from "react";
import api, { errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, Loader2, UserX } from "lucide-react";
import { toast } from "sonner";
import { useAuth, ROLE_LABEL, atLeast } from "@/lib/auth";
import { formatDate } from "@/lib/format";

const ROLES = ["super_admin", "partner", "manager", "article_assistant", "staff"];

export default function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: "", full_name: "", role: "staff", phone: "" });

  const load = () => api.get("/users").then(({ data }) => setUsers(data));
  useEffect(() => { load(); }, []);

  const invite = async () => {
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success(`Invitation sent to ${form.email}`);
      setOpen(false);
      setForm({ email: "", full_name: "", role: "staff", phone: "" });
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Failed to send invite"));
    } finally {
      setBusy(false);
    }
  };

  const updateRole = async (id, role) => {
    await api.patch(`/users/${id}`, { role });
    toast.success("Role updated");
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Deactivate this user?")) return;
    await api.delete(`/users/${id}`);
    toast.success("User removed");
    load();
  };

  const canManage = atLeast(user, "partner");

  return (
    <div className="space-y-4" data-testid="users-page">
      <div className="flex items-center gap-3">
        <p className="text-sm text-muted-foreground flex-1">Team members and their roles. Sign-in is invitation-only.</p>
        {canManage && (
          <Button size="sm" onClick={() => setOpen(true)} data-testid="new-user-btn">
            <Mail size={13} className="mr-1.5" /> Invite user
          </Button>
        )}
      </div>

      <div className="border border-border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-b border-border">
            <tr className="text-left">
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground">Name</th>
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground">Email</th>
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground">Role</th>
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground">Status</th>
              <th className="px-3 py-2 font-medium text-xs text-muted-foreground">Joined</th>
              {canManage && <th className="px-3 py-2 font-medium text-xs text-muted-foreground text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-border hover:bg-muted/40" data-testid={`user-row-${u.email}`}>
                <td className="px-3 py-2.5 font-medium">{u.full_name}</td>
                <td className="px-3 py-2.5 text-xs font-mono text-muted-foreground">{u.email}</td>
                <td className="px-3 py-2.5">
                  {canManage && u.id !== user.id ? (
                    <Select value={u.role} onValueChange={(v) => updateRole(u.id, v)}>
                      <SelectTrigger className="h-7 w-40 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge variant="secondary" className="text-[10px]">{ROLE_LABEL[u.role]}</Badge>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  <Badge variant={u.status === "active" ? "outline" : "destructive"} className="text-[10px]">{u.status}</Badge>
                </td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground font-mono">{formatDate(u.created_at, "dd MMM yy")}</td>
                {canManage && (
                  <td className="px-3 py-2.5 text-right">
                    {user.role === "super_admin" && u.id !== user.id && (
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => del(u.id)} data-testid={`user-delete-${u.email}`}>
                        <UserX size={13} />
                      </Button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="new-user-dialog">
          <DialogHeader>
            <DialogTitle>Invite team member</DialogTitle>
            <DialogDescription>They&rsquo;ll receive an email with sign-in instructions. Access is only granted once you invite them here.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Full name</Label>
              <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="new-user-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Work email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="colleague@yourfirm.com" data-testid="new-user-email" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={invite} disabled={!form.email || !form.full_name || busy} data-testid="new-user-submit">
              {busy && <Loader2 size={13} className="animate-spin mr-1.5" />}
              <Mail size={13} className="mr-1.5" /> Send invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
